import { ReceivableRow } from '@bariendo/lib/receivables';
import { Button, Group, Popover, Stack, Text, TextInput, UnstyledButton } from '@mantine/core';
import { useMedplum } from '@medplum/react';
import { IconFileTypePdf, IconPaperclip } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { CSSProperties, ReactElement, useEffect, useRef, useState } from 'react';
import { alert } from '@/services/alert';
import { fetchBot } from '@/services/services';
import { ORBIT_GRAY, STATUS_BADGE_TONES } from '@/theme/colors';
import { formatFileSize, MAX_PROOF_BYTES, readAsBase64 } from '../imports/manualEntry';
import { BILL_TO_LABEL, invoicedAmount, STATUS_LABEL, STATUS_TONE } from './receivableRows';
import { ReceivableActions } from './useReceivableActions';

/** The Status cell (BAR-3040): To invoice opens Mark invoiced, Invoiced opens Record payment, Paid is final. */

const BADGE_STYLE: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  fontSize: 12,
  lineHeight: '16px',
  fontWeight: 500,
  padding: '1px 9px',
  borderRadius: 100,
  whiteSpace: 'nowrap',
};

const SLOT_STYLE: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '10px 12px',
  border: '1px dashed rgba(0, 0, 0, 0.20)',
  borderRadius: 8,
  fontSize: 12,
  color: ORBIT_GRAY,
  width: '100%',
};

/** The amount typed, in dollars; 0 when it isn't a number. */
function parseAmount(value: string): number {
  const amount = Number.parseFloat(value.replace(/[^0-9.]/g, ''));
  return Number.isFinite(amount) ? Math.round(amount * 100) / 100 : 0;
}

const failure = (error: unknown) => (error instanceof Error ? error.message : 'unknown error');

function Badge({ row }: { row: ReceivableRow }): ReactElement {
  return (
    <span style={{ ...BADGE_STYLE, ...STATUS_BADGE_TONES[STATUS_TONE[row.status]] }}>{STATUS_LABEL[row.status]}</span>
  );
}

export interface ReceivableStatusCellProps {
  row: ReceivableRow;
  actions: ReceivableActions;
  now: Date;
}

export function ReceivableStatusCell({ row, actions, now }: ReceivableStatusCellProps): ReactElement {
  const [opened, setOpened] = useState(false);
  if (row.status === 'PAID') return <Badge row={row} />;

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-start"
      width={300}
      withinPortal
      shadow="md"
      transitionProps={{ duration: 0 }}
    >
      <Popover.Target>
        <UnstyledButton aria-label={STATUS_LABEL[row.status]} onClick={() => setOpened((open) => !open)}>
          <Badge row={row} />
        </UnstyledButton>
      </Popover.Target>
      <Popover.Dropdown>
        {opened &&
          (row.status === 'TO_INVOICE' ? (
            <MarkInvoicedForm row={row} actions={actions} now={now} onDone={() => setOpened(false)} />
          ) : (
            <RecordPaymentForm row={row} actions={actions} now={now} onDone={() => setOpened(false)} />
          ))}
      </Popover.Dropdown>
    </Popover>
  );
}

interface FormProps {
  row: ReceivableRow;
  actions: ReceivableActions;
  now: Date;
  onDone: () => void;
}

function MarkInvoicedForm({ row, actions, now, onDone }: FormProps): ReactElement {
  const medplum = useMedplum();
  const [invoicedOn, setInvoicedOn] = useState(dayjs(now).format('YYYY-MM-DD'));
  const [amount, setAmount] = useState(row.amount === null ? '' : row.amount.toFixed(2));
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [pdf, setPdf] = useState<File>();
  const [saving, setSaving] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    actions
      .nextInvoiceNumber(row)
      .then((next) => {
        if (!cancelled) setInvoiceNumber((typed) => typed || next);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [actions, row]);

  const attach = (file: File | undefined): void => {
    if (!file) return;
    if (file.type !== 'application/pdf') {
      alert.error('The invoice must be a PDF');
      return;
    }
    if (file.size > MAX_PROOF_BYTES) {
      alert.error(`The invoice is over ${formatFileSize(MAX_PROOF_BYTES)}`);
      return;
    }
    setPdf(file);
  };

  const submit = async (): Promise<void> => {
    setSaving(true);
    try {
      const invoiceBinaryId = pdf
        ? (
            await fetchBot<{ binaryId: string }>(medplum, 'finance-proof', {
              operation: 'upload',
              fileName: pdf.name,
              contentType: pdf.type,
              data: await readAsBase64(pdf),
            })
          ).binaryId
        : null;
      await actions.markInvoiced(row, {
        invoicedOn,
        invoicedAmount: parseAmount(amount),
        invoiceNumber: invoiceNumber.trim(),
        invoiceBinaryId,
      });
      onDone();
    } catch (error) {
      alert.error(`Couldn't mark it invoiced: ${failure(error)}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack gap={8}>
      <Text fz={13} fw={600}>
        Mark invoiced
      </Text>
      <TextInput
        label="Invoice date"
        size="xs"
        type="date"
        value={invoicedOn}
        onChange={(event) => setInvoicedOn(event.currentTarget.value)}
      />
      <TextInput
        label="Amount"
        size="xs"
        inputMode="decimal"
        placeholder="0.00"
        value={amount}
        onChange={(event) => setAmount(event.currentTarget.value)}
      />
      <TextInput
        label="Invoice number"
        size="xs"
        value={invoiceNumber}
        onChange={(event) => setInvoiceNumber(event.currentTarget.value)}
      />
      <input
        ref={fileInput}
        type="file"
        accept="application/pdf"
        hidden
        data-testid="invoice-input"
        onChange={(event) => attach(event.currentTarget.files?.[0])}
      />
      <UnstyledButton style={SLOT_STYLE} onClick={() => fileInput.current?.click()}>
        {pdf ? (
          <>
            <IconFileTypePdf size={14} />
            {`${pdf.name} · ${formatFileSize(pdf.size)}`}
          </>
        ) : (
          <>
            <IconPaperclip size={14} />
            Attach the invoice
          </>
        )}
      </UnstyledButton>
      <Group justify="flex-end" gap={8} mt={4}>
        <Button size="xs" variant="default" onClick={onDone}>
          Cancel
        </Button>
        <Button
          size="xs"
          color="dark"
          loading={saving}
          disabled={!invoicedOn || parseAmount(amount) <= 0 || !invoiceNumber.trim()}
          onClick={() => void submit()}
        >
          Mark invoiced
        </Button>
      </Group>
    </Stack>
  );
}

function RecordPaymentForm({ row, actions, now, onDone }: FormProps): ReactElement {
  const prefill = invoicedAmount(row);
  const [paidOn, setPaidOn] = useState(dayjs(now).format('YYYY-MM-DD'));
  const [amount, setAmount] = useState(prefill === null ? '' : prefill.toFixed(2));
  const [bankReference, setBankReference] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (): Promise<void> => {
    setSaving(true);
    try {
      await actions.recordPayment(row, {
        paidOn,
        paidAmount: parseAmount(amount),
        bankReference: bankReference.trim(),
      });
      onDone();
    } catch (error) {
      alert.error(`Couldn't record the payment: ${failure(error)}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack gap={8}>
      <Text fz={13} fw={600}>
        {`Record payment from ${BILL_TO_LABEL[row.billTo]}`}
      </Text>
      <TextInput
        label="Payment date"
        size="xs"
        type="date"
        value={paidOn}
        onChange={(event) => setPaidOn(event.currentTarget.value)}
      />
      <TextInput
        label="Amount received"
        size="xs"
        inputMode="decimal"
        placeholder="0.00"
        value={amount}
        onChange={(event) => setAmount(event.currentTarget.value)}
      />
      <TextInput
        label="Bank reference ID"
        size="xs"
        placeholder="From the bank statement"
        value={bankReference}
        onChange={(event) => setBankReference(event.currentTarget.value)}
      />
      <Group justify="flex-end" gap={8} mt={4}>
        <Button size="xs" variant="default" onClick={onDone}>
          Cancel
        </Button>
        <Button
          size="xs"
          color="dark"
          loading={saving}
          disabled={!paidOn || parseAmount(amount) <= 0 || !bankReference.trim()}
          onClick={() => void submit()}
        >
          Record payment
        </Button>
      </Group>
    </Stack>
  );
}
